import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminRegisterUser, uploadImage, formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, ArrowLeft, UserPlus, Camera, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATES = ["Tlaxcala","Puebla","CDMX","Estado de México","Veracruz","Hidalgo","Oaxaca","Otro"];

export default function RegisterPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const photoRef = useRef(null);
  const [form, setForm] = useState({ name:'', email:'', password:'', phone:'', curp:'', address:'', city:'', state:'Tlaxcala', birth_date:'', photo_url:'' });
  const [showPwd, setShowPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const { data } = await uploadImage(file); set('photo_url', data.path); toast.success('Foto cargada'); }
    catch (err) { toast.error('Error al subir foto'); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (!form.name || !form.email || !form.password) { setError('Nombre, email y contraseña son obligatorios'); return; }
    if (form.password.length < 6) { setError('Contraseña mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const { data } = await adminRegisterUser(form);
      setCreated(data);
      toast.success('Usuario registrado');
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!user || user.role !== 'admin') { nav('/login'); return null; }

  if (created) {
    return (
      <div data-testid="register-success" className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white border-2 border-[#0B1F40] p-8 brutal-shadow text-center">
          <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-xl text-[#0B1F40] mb-2" style={{fontFamily:'Archivo Black'}}>USUARIO REGISTRADO</h2>
          <div className="text-left bg-[#F4F5F7] border border-[#0B1F40]/10 p-4 mt-4 mb-4 text-xs space-y-1">
            <p><strong>Nombre:</strong> {created.name}</p>
            <p><strong>Email:</strong> {created.email}</p>
            <p><strong>ID:</strong> {created.user_id_code}</p>
            <p><strong>Teléfono:</strong> {created.phone || '-'}</p>
            <p><strong>CURP:</strong> {created.curp || '-'}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setCreated(null); setForm({ name:'', email:'', password:'', phone:'', curp:'', address:'', city:'', state:'Tlaxcala', birth_date:'', photo_url:'' }); }}
              className="flex-1 bg-[#0B1F40] text-white font-black text-xs h-10 brutal-btn-press" style={{boxShadow:'3px 3px 0 #E50B15'}}>REGISTRAR OTRO</button>
            <button onClick={() => nav('/admin')} className="flex-1 bg-[#E50B15] text-white font-black text-xs h-10 brutal-btn-press" style={{boxShadow:'3px 3px 0 #0B1F40'}}>IR AL ADMIN</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6"><ArrowLeft className="h-3 w-3" /> VOLVER</button>
      <div className="bg-white border-2 border-[#0B1F40] p-6 sm:p-8 brutal-shadow">
        <div className="flex items-center gap-2 mb-6">
          <UserPlus className="h-5 w-5 text-[#E50B15]" />
          <h1 className="text-xl text-[#0B1F40] tracking-tighter" style={{fontFamily:'Archivo Black'}}>REGISTRAR USUARIO</h1>
        </div>

        {error && <div data-testid="register-error" className="mb-4 p-3 bg-red-50 border-l-4 border-[#E50B15] text-[#E50B15] text-xs font-bold">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          {/* Photo */}
          <div className="flex items-center gap-4 mb-2">
            <div className="relative w-20 h-20 border-2 border-[#0B1F40]/20 bg-[#F4F5F7] flex items-center justify-center overflow-hidden shrink-0">
              {form.photo_url ? (
                <img src={form.photo_url.startsWith('http') ? form.photo_url : `/api/files/${form.photo_url}`} alt="Foto" className="w-full h-full object-cover" />
              ) : (
                <Camera className="h-6 w-6 text-[#0B1F40]/20" />
              )}
            </div>
            <div>
              <input ref={photoRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
              <button type="button" data-testid="upload-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploading}
                className="border-2 border-[#0B1F40]/20 text-[#0B1F40] text-[10px] font-black h-8 px-4 flex items-center gap-1.5 hover:border-[#0B1F40]">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />} SUBIR FOTO
              </button>
              <p className="text-[9px] text-[#0B1F40]/30 mt-1">JPG, PNG. Max 10MB</p>
            </div>
          </div>

          {/* Required */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">NOMBRE COMPLETO *</Label>
              <Input data-testid="register-name" value={form.name} onChange={e => set('name', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10 font-bold" required />
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">EMAIL *</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={e => set('email', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10 font-bold" required />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CONTRASEÑA *</Label>
              <div className="relative mt-1">
                <Input data-testid="register-password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                  className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 h-10 font-bold pr-10" required />
                <button type="button" data-testid="toggle-password-register" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center text-[#0B1F40]/40 hover:text-[#E50B15]">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">TELÉFONO</Label>
              <Input data-testid="register-phone" value={form.phone} onChange={e => set('phone', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" placeholder="2461234567" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CURP</Label>
              <Input data-testid="register-curp" value={form.curp} onChange={e => set('curp', e.target.value.toUpperCase())}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10 uppercase" maxLength={18} />
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">FECHA DE NACIMIENTO</Label>
              <Input data-testid="register-birth" type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">DIRECCIÓN</Label>
            <Input data-testid="register-address" value={form.address} onChange={e => set('address', e.target.value)}
              className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CIUDAD / MUNICIPIO</Label>
              <Input data-testid="register-city" value={form.city} onChange={e => set('city', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">ESTADO</Label>
              <Select value={form.state} onValueChange={v => set('state', v)}>
                <SelectTrigger data-testid="register-state" className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#0B1F40]">{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <button data-testid="register-submit" type="submit" disabled={loading}
            className="w-full bg-[#E50B15] text-white font-black text-sm h-11 flex items-center justify-center gap-2 brutal-btn-press transition-all mt-2" style={{boxShadow:'4px 4px 0px #0B1F40'}}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} REGISTRAR USUARIO
          </button>
        </form>
      </div>
    </div>
  );
}
